import { useAddProductToPlanMutation } from '@/core/api/graphql/forshawGraphqlApi.generated.ts'
import { useCallback, useState } from 'react'
import { useAccountBranchIdsParams } from '@/core/hooks/useAccountBranchIdsParams.ts'
import { replenishmentPlanActions } from '@/data/replenishmentPlan/slice/replenishmentPlan.slice.ts'
import { useDispatch } from 'react-redux'
import { getPlanMonthList } from '@/core/utils/getPlanMonthList.ts'
import { useGetPlanItemsForecastsShipments } from '@/data/replenishmentPlan/hooks/useGetPlanItemsForecastsShipments.ts'
import { mapReplenishmentPlanItemFragment } from '@/data/replenishmentPlan/mappers/mapReplenishmentPlanItemFragment.ts'
import { Product } from '@/data/replenishmentPlan/slice/replenishmentPlan.types.ts'

const useAddProductToPlan = () => {
  const [addProductToPlanMutation] = useAddProductToPlanMutation()
  const { branchId } = useAccountBranchIdsParams()

  const [isLoading, setIsLoading] = useState(false)

  const { fromMonthFormatted, toMonthFormatted } = getPlanMonthList()

  const { getPlanItemsForecastsShipments } = useGetPlanItemsForecastsShipments()

  const dispatch = useDispatch()

  const addProductToPlan = useCallback(
    async (product: Product) => {
      setIsLoading(true)

      try {
        const response = await addProductToPlanMutation({
          branchId,
          productId: product.id,
        })

        if ('error' in response) {
          return
        }

        const salableProductId =
          response.data?.addPlanProduct?.result?.salableProductId

        if (salableProductId == null) {
          throw new Error(
            `Product ${product.id} was added to the plan without a salableProductId`,
          )
        }

        const { planItems, forecasts, shipments } =
          await getPlanItemsForecastsShipments({
            branchId,
            fromMonth: fromMonthFormatted,
            toMonth: toMonthFormatted,
            productIds: [product.id],
          })

        const { forecastItems, shipmentItems, plannedItems } =
          mapReplenishmentPlanItemFragment(
            product,
            forecasts,
            planItems,
            shipments,
          )

        dispatch(
          replenishmentPlanActions.addProductToPlan({
            product,
            salableProductId,
            plannedItems,
            forecastItems,
            shipmentItems,
          }),
        )
      } catch (e) {
        console.error(e)
      } finally {
        setIsLoading(false)
      }
    },
    [
      addProductToPlanMutation,
      branchId,
      dispatch,
      fromMonthFormatted,
      getPlanItemsForecastsShipments,
      toMonthFormatted,
    ],
  )

  return { addProductToPlan, isLoading }
}

export { useAddProductToPlan }
